import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./AdvancedSettings.css";
import Slider from "../components/Slider";
import Modal from "../components/Modal";
import VideoModal from "../components/VideoModal";
import TimeSelector from "../components/TimeSelector";
import { lotPricingService, lotAdvancedPricingService } from "../utils/api";
import Error from "../components/Error";
import { useUser } from "../contexts/UserContext";
import { useDirtyState } from "../contexts/DirtyStateContext";

/**
 * Helper: parse "HH:MM" into total minutes (0..1439).
 */
function parseTime(str: string): number {
  if (!str || !str.includes(":")) return 0;
  const [hh, mm] = str.split(":").map(Number);
  const hSafe = isNaN(hh) ? 0 : Math.min(Math.max(hh, 0), 23);
  const mSafe = isNaN(mm) ? 0 : Math.min(Math.max(mm, 0), 59);
  return hSafe * 60 + mSafe;
}

type BlockMode = "default" | "allDay" | "setTime" | "newBlock" | "noTime" | "permitOnly";

interface Block {
  blockMode: BlockMode;    // <-- we store the mode in JSON so we can restore
  startTime?: string;
  endTime?: string;
  customRate?: string;
  customMax?: string;
  isDefault?: boolean;     // we'll keep this for backward compatibility
  permitTimeMode?: "allDay" | "setTime"; // New field for permit only time mode
}

/** 
 * We store up to 3 blocks. 
 */
type DayData = [Block, Block, Block];

const dayLabels = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/**
 * Sanitize currency input: allow only numbers and a single decimal point, and at most two decimal places.
 */
function sanitizeCurrencyInput(val: string, max: number): string {
  // Remove all non-numeric and non-decimal characters
  let sanitized = val.replace(/[^0-9.]/g, "");
  // Only keep the first decimal point
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    sanitized = parts[0] + '.' + parts.slice(1).join('');
  }
  // Limit to two decimal places
  if (sanitized.includes('.')) {
    const [intPart, decPart] = sanitized.split('.');
    sanitized = intPart + '.' + decPart.slice(0, 2);
  }
  // Remove leading zeros unless before decimal, but preserve single zero
  if (sanitized !== "0") {
    sanitized = sanitized.replace(/^0+(?!\.)/, '');
  }
  // Enforce max value
  if (sanitized && parseFloat(sanitized) > max) {
    sanitized = max.toString();
  }
  return sanitized;
}

/**
 * Convert the loaded JSON day array into our 3-block structure.
 * We look for `blockMode` if present; if not, we guess from isDefault or times.
 */
function parseDayPricing(dayPricing: any[], globalRate: string, globalMax: string): DayData {
  // If no data => 1 "default" block, then 2 "newBlock"
  if (!dayPricing || dayPricing.length === 0) {
    return [
      { blockMode: "default", customRate: globalRate, customMax: globalMax },
      { blockMode: "newBlock" },
      { blockMode: "newBlock" },
    ];
  }

  const blocks: Block[] = [];
  for (let i = 0; i < dayPricing.length && i < 3; i++) {
    const entry = dayPricing[i];

    // If the JSON already has a blockMode, use it
    let mode: BlockMode = "default";
    if (entry.blockMode) {
      mode = entry.blockMode;
    } else {
      // If no blockMode, infer from isDefault / times
      if (entry.isDefault) {
        mode = "default";
      } else if (entry.startTime && entry.endTime && entry.startTime === "00:00" && entry.endTime === "23:59") {
        mode = "allDay";
      } else if (entry.startTime && entry.endTime) {
        mode = "setTime";
      }
    }

    blocks.push({
      blockMode: mode,
      startTime: entry.startTime ?? "",
      endTime: entry.endTime ?? "",
      customRate: entry.hourlyRate ?? globalRate,
      customMax: entry.maximumAmount ?? globalMax,
      isDefault: !!entry.isDefault,
      permitTimeMode: mode === "permitOnly" ?
        (entry.startTime === "00:00" && entry.endTime === "23:59" ? "allDay" : "setTime") : undefined
    });
  }

  // Add missing block logic: if one or two setTime blocks exist, add a default remainder block
  if (blocks.length === 1 && blocks[0].blockMode === "setTime") {
    blocks.push({ blockMode: "default", customRate: globalRate, customMax: globalMax });
  } else if (blocks.length === 2 && blocks[0].blockMode === "setTime" && blocks[1].blockMode === "setTime") {
    blocks.push({ blockMode: "default", customRate: globalRate, customMax: globalMax });
  }

  // Add missing block logic for permitOnly blocks: if one or two permitOnly blocks exist, add a default remainder block
  // BUT only if they don't cover the full day (i.e., not allDay mode)
  if (blocks.length === 1 && blocks[0].blockMode === "permitOnly" && blocks[0].permitTimeMode !== "allDay") {
    blocks.push({ blockMode: "default", customRate: globalRate, customMax: globalMax });
  } else if (blocks.length === 2 && blocks[0].blockMode === "permitOnly" && blocks[1].blockMode === "permitOnly") {
    // Check if both blocks are allDay mode
    const bothAllDay = blocks[0].permitTimeMode === "allDay" && blocks[1].permitTimeMode === "allDay";
    if (!bothAllDay) {
      blocks.push({ blockMode: "default", customRate: globalRate, customMax: globalMax });
    }
  }

  // Special case: if first block is permitOnly with allDay mode, ensure second block is newBlock (no default)
  if (blocks.length >= 1 && blocks[0].blockMode === "permitOnly" &&
    blocks[0].permitTimeMode === "allDay" && blocks.length >= 2 &&
    blocks[1].blockMode === "default") {
    blocks[1] = { blockMode: "newBlock" };
  }

  // Fill the rest with newBlock
  while (blocks.length < 3) {
    blocks.push({ blockMode: "newBlock" });
  }

  return blocks as DayData;
}

/**
 * Build the array for JSON. We'll include "blockMode" so we can restore precisely.
 * If blockMode === "noTime", we skip it (or store it with zero coverage).
 * NEW: If blockMode === "default", we ALSO skip it - we don't need to store these as they use global values
 */
function buildDayPricing(blocks: DayData, globalRate: string, globalMax: string): any[] {
  const result: any[] = [];

  blocks.forEach((block) => {
    // Skip new blocks, no time blocks, and default blocks
    if (block.blockMode === "newBlock" || block.blockMode === "noTime" || block.blockMode === "default") {
      return; // skip
    }

    // If it's "allDay" => time is 00:00..23:59
    // If it's setTime => use block start/end
    // If it's permitOnly => use block start/end (always has time)
    let start = "00:00";
    let end = "23:59";
    if ((block.blockMode === "setTime" || block.blockMode === "permitOnly") && block.startTime && block.endTime) {
      // Ensure times are in HH:mm format
      const formatTime = (time: string) => {
        const [hours, minutes] = time.split(":").map(Number);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      };
      start = formatTime(block.startTime);
      end = formatTime(block.endTime);
    }

    const hrRate = block.customRate ?? globalRate;
    const maxAmt = block.customMax ?? globalMax;

    // For permitOnly, we don't include rate and max amount
    if (block.blockMode === "permitOnly") {
      result.push({
        blockMode: block.blockMode,
        startTime: start,
        endTime: end,
        hourlyRate: "",
        maximumAmount: "",
        isDefault: false,
      });
    } else if ((hrRate !== undefined && hrRate !== null && hrRate !== "") && start && end) {
      // Only add if we have valid rate (including 0) and times for non-permit blocks
      result.push({
        blockMode: block.blockMode,
        startTime: start,
        endTime: end,
        hourlyRate: hrRate,
        maximumAmount: maxAmt,
        isDefault: false,
      });
    }
  });

  return result;
}

/**
 * Convert API LotAdvancedPricing entries to the format expected by frontend
 */
function mapApiToFrontendFormat(apiPricings: any[], dayOfWeek: string): any[] {
  return apiPricings
    .filter(pricing => pricing.dayOfWeek === dayOfWeek)
    .map(pricing => {
      // Handle time strings directly
      let startTime = "00:00";
      let endTime = "23:59";

      if (pricing.hourStart) {
        // If it's a string, use it directly
        if (typeof pricing.hourStart === 'string') {
          startTime = pricing.hourStart.substring(0, 5); // Take only HH:mm part
        } else if (pricing.hourStart.hour !== undefined) {
          // If it's an object with hour/minute, format it
          const hour = pricing.hourStart.hour.toString().padStart(2, '0');
          const minute = pricing.hourStart.minute.toString().padStart(2, '0');
          startTime = `${hour}:${minute}`;
        }
      }

      if (pricing.hourEnd) {
        // If it's a string, use it directly
        if (typeof pricing.hourEnd === 'string') {
          endTime = pricing.hourEnd.substring(0, 5); // Take only HH:mm part
        } else if (pricing.hourEnd.hour !== undefined) {
          // If it's an object with hour/minute, format it
          const hour = pricing.hourEnd.hour.toString().padStart(2, '0');
          const minute = pricing.hourEnd.minute.toString().padStart(2, '0');
          endTime = `${hour}:${minute}`;
        }
      }

      // Map block modes to match frontend expectations
      const blockMode = pricing.blockMode === "Default" ? "default" :
        pricing.blockMode === "AllDay" ? "allDay" :
          pricing.blockMode === "PermitOnly" ? "permitOnly" : "setTime";

      return {
        blockMode: blockMode,
        startTime: startTime,
        endTime: endTime,
        hourlyRate: pricing.rate !== null ? pricing.rate.toString() : "",
        maximumAmount: pricing.dailyMaximumPrice !== null ? pricing.dailyMaximumPrice.toString() : "",
        isDefault: blockMode === "default",
        permitTimeMode: blockMode === "permitOnly" ?
          (startTime === "00:00" && endTime === "23:59" ? "allDay" : "setTime") : undefined
      };
    });
}

/**
 * Convert frontend pricing format to API format
 */
function mapFrontendToApiFormat(dayPricing: any[], dayOfWeek: string): any[] {
  return dayPricing.map(pricing => {
    // Ensure we have valid time strings
    const startTime = pricing.startTime && pricing.startTime.trim() ? pricing.startTime.trim() : "00:00";
    const endTime = pricing.endTime && pricing.endTime.trim() ? pricing.endTime.trim() : "23:59";

    // Parse rate and max amount
    const rate = parseFloat(pricing.hourlyRate) || 0;
    const dailyMaximumPrice = pricing.maximumAmount ? parseFloat(pricing.maximumAmount) : null;

    // Map block modes to match API expectations
    const blockMode = pricing.blockMode === "default" ? "Default" :
      pricing.blockMode === "allDay" ? "AllDay" :
        pricing.blockMode === "permitOnly" ? "PermitOnly" : "SetTime";

    // Format time as HH:mm:ss
    const formatTime = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(":").map(Number);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    };

    // Create the API DTO with only the fields expected by the backend
    return {
      dayOfWeek: dayOfWeek,
      hourStart: formatTime(startTime),
      hourEnd: formatTime(endTime),
      rate: blockMode === "PermitOnly" ? 0 : rate,
      dailyMaximumPrice: blockMode === "PermitOnly" ? 0 : dailyMaximumPrice,
      blockMode: blockMode
    };
  });
}

const AdvancedSettings: React.FC = () => {
  const { customerId, lotId } = useParams<{ customerId: string; lotId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // General pricing data
  const [generalPricing, setGeneralPricing] = useState<any>(null);
  const [advancedPricing, setAdvancedPricing] = useState<any[]>([]);

  // Get values from general pricing
  const globalRate = generalPricing?.hourlyRate !== undefined ? String(generalPricing.hourlyRate) : "";
  const globalMax = generalPricing?.dailyMaximumPrice !== undefined ? String(generalPricing.dailyMaximumPrice) : "";

  // Parse day arrays from API data
  const [mondayBlocks, setMondayBlocks] = useState<DayData>(() =>
    parseDayPricing([], globalRate, globalMax)
  );
  const [tuesdayBlocks, setTuesdayBlocks] = useState<DayData>(() =>
    parseDayPricing([], globalRate, globalMax)
  );
  const [wednesdayBlocks, setWednesdayBlocks] = useState<DayData>(() =>
    parseDayPricing([], globalRate, globalMax)
  );
  const [thursdayBlocks, setThursdayBlocks] = useState<DayData>(() =>
    parseDayPricing([], globalRate, globalMax)
  );
  const [fridayBlocks, setFridayBlocks] = useState<DayData>(() =>
    parseDayPricing([], globalRate, globalMax)
  );
  const [saturdayBlocks, setSaturdayBlocks] = useState<DayData>(() =>
    parseDayPricing([], globalRate, globalMax)
  );
  const [sundayBlocks, setSundayBlocks] = useState<DayData>(() =>
    parseDayPricing([], globalRate, globalMax)
  );

  // FIX: Set advanced enabled state from API response
  // Determine if advanced is on/off
  const [advancedEnabled, setAdvancedEnabled] = useState(false);

  // Walkthrough integration: when the walkthrough requests it, force-enable
  // advanced pricing locally (no server call — just shows the grid).
  useEffect(() => {
    const handler = () => setAdvancedEnabled(true);
    window.addEventListener("walkthrough-enable-advanced", handler);
    return () => window.removeEventListener("walkthrough-enable-advanced", handler);
  }, []);

  // Also force-enable on mount if the walkthrough is already active (covers
  // the case where the user navigates here after the step's onEnter event
  // already fired).
  useEffect(() => {
    if (!isLoading && document.body.classList.contains("walkthrough-active")) {
      setAdvancedEnabled(true);
    }
  }, [isLoading]);

  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  type ModalType = null | "disableAdvanced" | "confirmSave" | "unsavedChanges" | "confirmReset" | "tutorialVideo";
  const [modalType, setModalType] = useState<ModalType>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // Add dirty state context
  const { setIsDirty: setGlobalDirty } = useDirtyState();

  // Update useEffect to sync local and global dirty state
  useEffect(() => {
    setGlobalDirty(isDirty);
  }, [isDirty, setGlobalDirty]);

  // Add cleanup effect to clear dirty state when component unmounts
  useEffect(() => {
    return () => {
      setGlobalDirty(false);
    };
  }, [setGlobalDirty]);

  // Load both general and advanced pricing data from API
  useEffect(() => {
    const loadData = async () => {
      if (!lotId) return;

      try {
        setIsLoading(true);
        setError(null);

        // Load general pricing first
        const generalData = await lotPricingService.getLatestPricingByLotId(lotId);
        setGeneralPricing(generalData);

        // Load advanced pricing status
        const isAdvancedEnabled = await lotAdvancedPricingService.getAdvancedPricingStatus(lotId);
        setAdvancedEnabled(isAdvancedEnabled);

        // Load advanced pricing data
        const advancedData = await lotAdvancedPricingService.getActiveAdvancedPricing(lotId);
        setAdvancedPricing(advancedData);

        // Initialize all day blocks
        const gRate = generalData?.hourlyRate !== undefined ? String(generalData.hourlyRate) : "";
        const gMax = generalData?.dailyMaximumPrice !== undefined ? String(generalData.dailyMaximumPrice) : "";

        // Parse data for each day
        setMondayBlocks(parseDayPricing(mapApiToFrontendFormat(advancedData, "MONDAY"), gRate, gMax));
        setTuesdayBlocks(parseDayPricing(mapApiToFrontendFormat(advancedData, "TUESDAY"), gRate, gMax));
        setWednesdayBlocks(parseDayPricing(mapApiToFrontendFormat(advancedData, "WEDNESDAY"), gRate, gMax));
        setThursdayBlocks(parseDayPricing(mapApiToFrontendFormat(advancedData, "THURSDAY"), gRate, gMax));
        setFridayBlocks(parseDayPricing(mapApiToFrontendFormat(advancedData, "FRIDAY"), gRate, gMax));
        setSaturdayBlocks(parseDayPricing(mapApiToFrontendFormat(advancedData, "SATURDAY"), gRate, gMax));
        setSundayBlocks(parseDayPricing(mapApiToFrontendFormat(advancedData, "SUNDAY"), gRate, gMax));

      } catch (err) {
        console.error("Error loading pricing data:", err);
        setError("Failed to load lot pricing data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [lotId]);

  // Update day blocks when general pricing changes
  useEffect(() => {
    if (generalPricing) {
      const gRate = String(generalPricing.hourlyRate || "");
      const gMax = String(generalPricing.dailyMaximumPrice || "");

      // Only update rates for default blocks to avoid overwriting custom rates
      const updateDefaultRates = (blocks: DayData): DayData => {
        return blocks.map(block => {
          if (block.blockMode === "default") {
            return { ...block, customRate: gRate, customMax: gMax };
          }
          return block;
        }) as DayData;
      };

      setMondayBlocks(prev => updateDefaultRates(prev));
      setTuesdayBlocks(prev => updateDefaultRates(prev));
      setWednesdayBlocks(prev => updateDefaultRates(prev));
      setThursdayBlocks(prev => updateDefaultRates(prev));
      setFridayBlocks(prev => updateDefaultRates(prev));
      setSaturdayBlocks(prev => updateDefaultRates(prev));
      setSundayBlocks(prev => updateDefaultRates(prev));
    }
  }, [generalPricing]);

  const dayStates = [
    { label: "Monday", blocks: mondayBlocks, setter: setMondayBlocks },
    { label: "Tuesday", blocks: tuesdayBlocks, setter: setTuesdayBlocks },
    { label: "Wednesday", blocks: wednesdayBlocks, setter: setWednesdayBlocks },
    { label: "Thursday", blocks: thursdayBlocks, setter: setThursdayBlocks },
    { label: "Friday", blocks: fridayBlocks, setter: setFridayBlocks },
    { label: "Saturday", blocks: saturdayBlocks, setter: setSaturdayBlocks },
    { label: "Sunday", blocks: sundayBlocks, setter: setSundayBlocks },
  ];

  // Clipboard state for copy/paste functionality
  const [copiedDayData, setCopiedDayData] = useState<DayData | null>(null);
  const [hoveredDayIndex, setHoveredDayIndex] = useState<number | null>(null);

  // Feedback tooltip state
  const [tooltipMessage, setTooltipMessage] = useState<string>("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipDayIndex, setTooltipDayIndex] = useState<number>(0);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Undo functionality
  const [actionHistory, setActionHistory] = useState<Array<{
    dayIndex: number;
    previousBlocks: DayData;
    action: 'paste' | 'manual';
  }>>([]);

  // Show tooltip feedback under the specified day column
  const showFeedback = (message: string, dayIndex: number) => {
    setTooltipMessage(message);
    setTooltipDayIndex(dayIndex);

    // Calculate position under the day header
    const dayHeader = document.querySelector(`[data-day-index="${dayIndex}"]`) as HTMLElement;
    if (dayHeader) {
      const rect = dayHeader.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.bottom + -5
      });
    }

    setShowTooltip(true);

    // Start fade out after 400ms (allowing 100ms for fade out animation)
    setTimeout(() => {
      const tooltip = document.querySelector('.CopyPasteTooltip') as HTMLElement;
      if (tooltip) {
        tooltip.classList.add('fade-out');
      }
      // Remove from DOM after fade out animation completes
      setTimeout(() => {
        setShowTooltip(false);
      }, 200);
    }, 400);
  };

  // Save state for undo
  const saveStateForUndo = (dayIndex: number, action: 'paste' | 'manual') => {
    const currentBlocks = dayStates[dayIndex].blocks;
    setActionHistory(prev => [...prev, {
      dayIndex,
      previousBlocks: [...currentBlocks] as DayData,
      action
    }]);
  };

  // Handle copy/paste keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && hoveredDayIndex !== null) {
        if (event.key === 'c') {
          event.preventDefault();
          // Copy the day data
          const dayData = dayStates[hoveredDayIndex];
          setCopiedDayData([...dayData.blocks] as DayData);
          showFeedback('Copied!', hoveredDayIndex);
        } else if (event.key === 'v' && copiedDayData) {
          event.preventDefault();
          // Save current state for undo
          saveStateForUndo(hoveredDayIndex, 'paste');

          // Paste the day data
          const dayData = dayStates[hoveredDayIndex];
          const newBlocks = copiedDayData.map(block => ({
            ...block,
            customRate: block.customRate || globalRate,
            customMax: block.customMax || globalMax,
          })) as DayData;
          dayData.setter(newBlocks);
          setIsDirty(true);
          showFeedback('Pasted!', hoveredDayIndex);
        } else if (event.key === 'z') {
          event.preventDefault();
          // Undo last action
          if (actionHistory.length > 0) {
            const lastAction = actionHistory[actionHistory.length - 1];
            const dayData = dayStates[lastAction.dayIndex];
            dayData.setter(lastAction.previousBlocks);
            setIsDirty(true);
            showFeedback('Undone!', lastAction.dayIndex);

            // Remove the action from history
            setActionHistory(prev => prev.slice(0, -1));
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hoveredDayIndex, copiedDayData, dayStates, globalRate, globalMax, setIsDirty, actionHistory]);

  // ------------- HANDLERS -------------
  const handleToggleAdvanced = () => {
    if (!advancedEnabled) {
      setAdvancedEnabled(true);
      setIsDirty(true);
    } else {
      setModalType("disableAdvanced");
    }
  };

  async function confirmDisableAdvanced() {
    if (!lotId || !user?.userId) return;

    try {
      setIsSaving(true);

      // Reset each day => top block default, bottom blocks newBlock
      dayStates.forEach((day) => {
        day.setter([
          { blockMode: "default", customRate: globalRate, customMax: globalMax },
          { blockMode: "newBlock" },
          { blockMode: "newBlock" },
        ]);
      });

      // Update advanced pricing status
      await lotAdvancedPricingService.changeAdvancedPricingStatus(lotId, user.userId, false);
      setAdvancedEnabled(false);

      setIsDirty(false);
    } catch (error) {
      alert("Error disabling advanced settings.");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  function handleSaveClick() {
    const inWalkthrough =
      typeof document !== "undefined" &&
      document.body.classList.contains("walkthrough-active");
    if (inWalkthrough) {
      doSave();
      return;
    }
    setModalType("confirmSave");
  }

  async function doSave() {
    if (!lotId || !user?.userId) {
      alert("No lot ID or user ID found");
      return;
    }

    try {
      setIsSaving(true);

      // First, enable advanced pricing for the lot
      try {
        await lotAdvancedPricingService.changeAdvancedPricingStatus(lotId, user.userId, true);
        console.log("Advanced pricing enabled for lot:", lotId);
      } catch (error: any) {
        console.error("Error enabling advanced pricing:", error);
        throw error; // Re-throw the original error
      }

      // Then, delete any existing advanced pricing entries
      try {
        await lotAdvancedPricingService.deleteActiveAdvancedPricing(lotId, user.userId);
      } catch (error: any) {
        console.warn("Error deleting existing pricing, continuing with save:", error);
      }

      // Prepare all day blocks for the API
      const allPricingDTOs = [
        ...buildDayPricing(mondayBlocks, globalRate, globalMax).map(p => mapFrontendToApiFormat([p], "MONDAY")[0]),
        ...buildDayPricing(tuesdayBlocks, globalRate, globalMax).map(p => mapFrontendToApiFormat([p], "TUESDAY")[0]),
        ...buildDayPricing(wednesdayBlocks, globalRate, globalMax).map(p => mapFrontendToApiFormat([p], "WEDNESDAY")[0]),
        ...buildDayPricing(thursdayBlocks, globalRate, globalMax).map(p => mapFrontendToApiFormat([p], "THURSDAY")[0]),
        ...buildDayPricing(fridayBlocks, globalRate, globalMax).map(p => mapFrontendToApiFormat([p], "FRIDAY")[0]),
        ...buildDayPricing(saturdayBlocks, globalRate, globalMax).map(p => mapFrontendToApiFormat([p], "SATURDAY")[0]),
        ...buildDayPricing(sundayBlocks, globalRate, globalMax).map(p => mapFrontendToApiFormat([p], "SUNDAY")[0]),
      ];

      // Log the data being sent
      console.log("Sending advanced pricing data:", JSON.stringify(allPricingDTOs, null, 2));

      // Only proceed if we have at least one pricing block
      if (allPricingDTOs.length > 0) {
        // Create new advanced pricing entries
        const response = await lotAdvancedPricingService.createAdvancedPricing(lotId, allPricingDTOs, user.userId);
        console.log("Create advanced pricing response:", response);
      } else {
        // If no advanced pricing, disable advanced settings
        await lotAdvancedPricingService.changeAdvancedPricingStatus(lotId, user.userId, false);
      }

      setIsDirty(false);
      setModalType(null);

    } catch (error: any) {
      console.error("Error saving advanced settings:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
      }
      alert("Failed to update advanced settings. Please try again.");
      setModalType(null);
    } finally {
      setIsSaving(false);
    }
  }

  function handleNavigation(path: string) {
    if (isDirty) {
      setPendingAction(() => () => navigate(path));
      setModalType("unsavedChanges");
    } else {
      navigate(path);
    }
  }

  function handleGeneralSettings() {
    if (customerId && lotId) {
      handleNavigation(`/${customerId}/${lotId}/settings`);
    } else {
      alert("No lot selected!");
    }
  }

  // Add this function near the other handlers
  function handleReset() {
    setModalType("confirmReset");
  }

  function confirmReset() {
    // Reset each day to default state
    dayStates.forEach((day) => {
      day.setter([
        { blockMode: "default", customRate: globalRate, customMax: globalMax },
        { blockMode: "newBlock" },
        { blockMode: "newBlock" },
      ]);
    });
    setIsDirty(false);
    setModalType(null);
  }

  // Add this function to check if grid has modifications
  function hasGridModifications(): boolean {
    return dayStates.some(({ blocks }) => {
      // Check if any block is not in default state
      return blocks.some((block, index) => {
        if (index === 0) {
          // First block should be default
          return block.blockMode !== "default";
        } else {
          // Other blocks should be newBlock
          return block.blockMode !== "newBlock";
        }
      });
    });
  }

  // ------------ COVERAGE LOGIC -----------
  /** 
   * Check if top 2 setTime blocks sum 24h => bottom row => noTime 
   * Otherwise => bottom row => default remainder 
   */
  function maybeAdjustThirdRow(blocks: DayData) {
    const top = blocks[0];
    const mid = blocks[1];
    const bottom = blocks[2];

    // If top/mid both setTime, compute coverage
    if (top.blockMode === "setTime" && mid.blockMode === "setTime") {
      const startA = parseTime(top.startTime ?? "");
      const endA = parseTime(top.endTime ?? "");
      const durA = (endA - startA + 1440) % 1440;

      const startB = parseTime(mid.startTime ?? "");
      const endB = parseTime(mid.endTime ?? "");
      const durB = (endB - startB + 1440) % 1440;

      if (durA + durB === 1440) {
        // covers full day => bottom => noTime
        if (bottom.blockMode !== "noTime") {
          blocks[2] = { blockMode: "noTime" }; // "No Remaining Time Available"
        }
      } else {
        // partial coverage => bottom => default remainder
        if (bottom.blockMode === "noTime" || bottom.blockMode === "newBlock") {
          blocks[2] = {
            blockMode: "default",
            customRate: globalRate,
            customMax: globalMax,
          };
        }
      }
    } else if (top.blockMode === "permitOnly" && mid.blockMode === "permitOnly") {
      // If top/mid both permitOnly, compute coverage
      const startA = parseTime(top.startTime ?? "");
      const endA = parseTime(top.endTime ?? "");
      const durA = (endA - startA + 1440) % 1440;

      const startB = parseTime(mid.startTime ?? "");
      const endB = parseTime(mid.endTime ?? "");
      const durB = (endB - startB + 1440) % 1440;

      if (durA + durB === 1440) {
        // covers full day => bottom => noTime
        if (bottom.blockMode !== "noTime") {
          blocks[2] = { blockMode: "noTime" }; // "No Remaining Time Available"
        }
      } else {
        // partial coverage => bottom => default remainder
        if (bottom.blockMode === "noTime" || bottom.blockMode === "newBlock") {
          blocks[2] = {
            blockMode: "default",
            customRate: globalRate,
            customMax: globalMax,
          };
        }
      }
    } else if ((top.blockMode === "setTime" && mid.blockMode === "permitOnly") ||
      (top.blockMode === "permitOnly" && mid.blockMode === "setTime")) {
      // If top/mid are mix of setTime and permitOnly, compute coverage
      const startA = parseTime(top.startTime ?? "");
      const endA = parseTime(top.endTime ?? "");
      const durA = (endA - startA + 1440) % 1440;

      const startB = parseTime(mid.startTime ?? "");
      const endB = parseTime(mid.endTime ?? "");
      const durB = (endB - startB + 1440) % 1440;

      if (durA + durB === 1440) {
        // covers full day => bottom => noTime
        if (bottom.blockMode !== "noTime") {
          blocks[2] = { blockMode: "noTime" }; // "No Remaining Time Available"
        }
      } else {
        // partial coverage => bottom => default remainder
        if (bottom.blockMode === "noTime" || bottom.blockMode === "newBlock") {
          blocks[2] = {
            blockMode: "default",
            customRate: globalRate,
            customMax: globalMax,
          };
        }
      }
    } else {
      // If top or mid is not setTime or permitOnly => revert bottom => default remainder if it was "noTime"
      if (bottom.blockMode === "noTime") {
        blocks[2] = {
          blockMode: "default",
          customRate: globalRate,
          customMax: globalMax,
        };
      }
    }
  }

  // Add helper function to generate time options and calculate disabled times
  function getDisabledTimes(startTime: string, endTime: string, isStartSelector: boolean): string[] {
    const disabledTimes: string[] = [];

    if (isStartSelector && endTime && endTime !== "--") {
      // For start time selector, disable times >= end time
      for (let hour = 0; hour < 24; hour++) {
        // Add both whole hours and half hours
        const wholeHourStr = `${hour.toString().padStart(2, '0')}:00`;
        const halfHourStr = `${hour.toString().padStart(2, '0')}:30`;

        if (wholeHourStr >= endTime) {
          disabledTimes.push(wholeHourStr);
        }
        if (halfHourStr >= endTime) {
          disabledTimes.push(halfHourStr);
        }
      }
    } else if (!isStartSelector && startTime && startTime !== "--") {
      // For end time selector, disable times <= start time
      for (let hour = 0; hour < 24; hour++) {
        // Add both whole hours and half hours
        const wholeHourStr = `${hour.toString().padStart(2, '0')}:00`;
        const halfHourStr = `${hour.toString().padStart(2, '0')}:30`;

        if (wholeHourStr <= startTime) {
          disabledTimes.push(wholeHourStr);
        }
        if (halfHourStr <= startTime) {
          disabledTimes.push(halfHourStr);
        }
      }
    }

    return disabledTimes;
  }

  // Add validation function for setTime blocks
  function validateSetTimeBlocks(): boolean {
    const allSetTimeBlocks = [
      ...mondayBlocks.filter(b => b.blockMode === "setTime"),
      ...tuesdayBlocks.filter(b => b.blockMode === "setTime"),
      ...wednesdayBlocks.filter(b => b.blockMode === "setTime"),
      ...thursdayBlocks.filter(b => b.blockMode === "setTime"),
      ...fridayBlocks.filter(b => b.blockMode === "setTime"),
      ...saturdayBlocks.filter(b => b.blockMode === "setTime"),
      ...sundayBlocks.filter(b => b.blockMode === "setTime"),
    ];

    const allPermitSetTimeBlocks = [
      ...mondayBlocks.filter(b => b.blockMode === "permitOnly" && b.permitTimeMode === "setTime"),
      ...tuesdayBlocks.filter(b => b.blockMode === "permitOnly" && b.permitTimeMode === "setTime"),
      ...wednesdayBlocks.filter(b => b.blockMode === "permitOnly" && b.permitTimeMode === "setTime"),
      ...thursdayBlocks.filter(b => b.blockMode === "permitOnly" && b.permitTimeMode === "setTime"),
      ...fridayBlocks.filter(b => b.blockMode === "permitOnly" && b.permitTimeMode === "setTime"),
      ...saturdayBlocks.filter(b => b.blockMode === "permitOnly" && b.permitTimeMode === "setTime"),
      ...sundayBlocks.filter(b => b.blockMode === "permitOnly" && b.permitTimeMode === "setTime"),
    ];

    const allBlocksToValidate = [...allSetTimeBlocks, ...allPermitSetTimeBlocks];

    return allBlocksToValidate.every(block =>
      block.startTime && block.startTime !== "--" &&
      block.endTime && block.endTime !== "--"
    );
  }

  // -------------- RENDER UTILS -------------
  const modeLabelMap: Record<BlockMode, string> = {
    default: "Default",
    allDay: "All Day",
    setTime: "Set Time",
    newBlock: "New Time Block",
    noTime: "No Remaining Time Available",
    permitOnly: "Permit Only",
  };

  function renderBlockDropdown(
    block: Block,
    rowIndex: number,
    blocks: DayData,
    setBlocks: React.Dispatch<React.SetStateAction<DayData>>
  ) {
    if (block.blockMode === "newBlock" || block.blockMode === "noTime") {
      return null; // hide entirely
    }
    // If rowIndex=2 and it's default => remainder => hide dropdown
    if (rowIndex === 2 && block.blockMode === "default") {
      return null;
    }

    const options: BlockMode[] =
      rowIndex === 0 ? ["default", "allDay", "setTime", "permitOnly"] : ["default", "setTime", "permitOnly"];

    function handleChange(newMode: BlockMode) {
      // Save state for undo
      const dayIndex = dayStates.findIndex(day => day.blocks === blocks);
      if (dayIndex !== -1) {
        saveStateForUndo(dayIndex, 'manual');
      }

      const newBlocks = [...blocks] as DayData;
      if (newMode === "default") {
        newBlocks[rowIndex] = {
          blockMode: "default",
          customRate: globalRate,
          customMax: globalMax,
        };
      } else if (newMode === "permitOnly") {
        newBlocks[rowIndex] = {
          blockMode: "permitOnly",
          startTime: rowIndex === 0 ? "00:00" : "--",
          endTime: rowIndex === 0 ? "23:59" : "--",
          permitTimeMode: rowIndex === 0 ? "allDay" : "setTime",
        };
      } else {
        newBlocks[rowIndex] = { ...newBlocks[rowIndex], blockMode: newMode };
      }

      // same forced logic
      if (rowIndex === 0 && newMode === "setTime") {
        newBlocks[1] = { blockMode: "default", customRate: globalRate, customMax: globalMax };
        newBlocks[2] = { blockMode: "newBlock" };
      } else if (rowIndex === 0 && (newMode === "allDay" || newMode === "default")) {
        newBlocks[1] = { blockMode: "newBlock" };
        newBlocks[2] = { blockMode: "newBlock" };
      } else if (rowIndex === 0 && newMode === "permitOnly") {
        // For permitOnly, don't add any blocks below initially
        newBlocks[1] = { blockMode: "newBlock" };
        newBlocks[2] = { blockMode: "newBlock" };
      } else if (rowIndex === 1 && newMode === "setTime") {
        newBlocks[2] = { blockMode: "default", customRate: globalRate, customMax: globalMax };
      } else if (rowIndex === 1 && newMode === "permitOnly") {
        newBlocks[2] = { blockMode: "default", customRate: globalRate, customMax: globalMax };
      } else if (rowIndex === 1 && (newMode === "allDay" || newMode === "default")) {
        newBlocks[2] = { blockMode: "newBlock" };
      }

      setBlocks(newBlocks);
      setIsDirty(true);

      // Notify the walkthrough when Monday's first block becomes Set Time.
      if (newMode === "setTime" && rowIndex === 0) {
        const dayIndex = dayStates.findIndex((d) => d.blocks === blocks);
        if (dayIndex === 0 && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("walkthrough-advanced-set-time"));
        }
      }
    }

    return (
      <select
        className={
          "block-mode-dropdown" + (block.blockMode === "setTime" ? " settime-dropdown" : "")
        }
        value={block.blockMode}
        onChange={(e) => handleChange(e.target.value as BlockMode)}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {modeLabelMap[opt]}
          </option>
        ))}
      </select>
    );
  }

  function renderBlockContent(
    block: Block,
    rowIndex: number,
    blocks: DayData,
    setBlocks: React.Dispatch<React.SetStateAction<DayData>>
  ) {
    // noTime => show "No Remaining Time Available"
    if (block.blockMode === "noTime") {
      return (
        <div className="fixed-box new-block-mode">
          <div className="new-block">No Remaining Time Available</div>
        </div>
      );
    }
    if (block.blockMode === "newBlock") {
      return (
        <div className="fixed-box new-block-mode">
          <div className="new-block">New Time Block</div>
        </div>
      );
    }

    if (block.blockMode === "default") {
      const labelText = rowIndex === 0 ? "All Day" : "Remainder";
      const isRemainder = rowIndex === 2;
      return (
        <div className={`fixed-box allDay-mode${isRemainder ? ' remainder-block' : ''}`}>
          <div className="allDay-bottom">
            <label className="small-label">{labelText}</label>
            <div className="price-row">
              <input
                type="number"
                className="block-input"
                value={block.customRate ?? ""}
                disabled
              />
              <span className="block-unit">$/hr</span>
            </div>
            <div className="price-row">
              <input
                type="number"
                className="block-input-bottom"
                value={block.customMax ?? ""}
                disabled
              />
              <span className="block-unit">$ Max</span>
            </div>
          </div>
        </div>
      );
    }

    if (block.blockMode === "allDay") {
      const handleRateChange = (val: string) => {
        const newRate = sanitizeCurrencyInput(val, 150);
        const maxAmount = parseFloat(block.customMax || "0");
        let finalRate = newRate;
        // Prevent hourly rate from exceeding maximum amount
        if (newRate && maxAmount && parseFloat(newRate) > maxAmount) {
          finalRate = block.customMax || "";
        }
        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = {
          ...block,
          customRate: finalRate,
        };
        setBlocks(newBlocks);
        setIsDirty(true);
      };
      const handleMaxChange = (val: string) => {
        const newMax = sanitizeCurrencyInput(val, 999);
        const currentRate = parseFloat(block.customRate || "0");
        let adjustedRate = block.customRate;
        if (newMax && currentRate && parseFloat(newMax) < currentRate) {
          adjustedRate = newMax;
        }
        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = {
          ...block,
          customMax: newMax,
          customRate: adjustedRate,
        };
        setBlocks(newBlocks);
        setIsDirty(true);
      };
      return (
        <div className="fixed-box allDay-mode">
          <div className="allDay-bottom">
            <div className="price-row">
              <input
                type="text"
                className="block-input"
                placeholder="Rate"
                value={sanitizeCurrencyInput(block.customRate ?? "", 150)}
                onChange={(e) => handleRateChange(e.target.value)}
                maxLength={7}
              />
              <span className="block-unit">$/hr</span>
            </div>
            <div className="price-row">
              <input
                type="text"
                className="block-input-bottom"
                placeholder="Max"
                value={sanitizeCurrencyInput(block.customMax ?? "", 999)}
                onChange={(e) => handleMaxChange(e.target.value)}
                maxLength={7}
              />
              <span className="block-unit">$ Max</span>
            </div>
          </div>
        </div>
      );
    }

    if (block.blockMode === "setTime") {
      const handleStartChange = (val: string) => {
        // If end time is set and new start time is >= end time, don't allow the change
        if (block.endTime && block.endTime !== "--" && val >= block.endTime) {
          return; // Prevent invalid selection
        }

        // Save state for undo
        const dayIndex = dayStates.findIndex(day => day.blocks === blocks);
        if (dayIndex !== -1) {
          saveStateForUndo(dayIndex, 'manual');
        }

        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = { ...block, startTime: val };
        setBlocks(newBlocks);
        setIsDirty(true);
      };

      const handleEndChange = (val: string) => {
        // If start time is set and new end time is <= start time, don't allow the change
        if (block.startTime && block.startTime !== "--" && val <= block.startTime) {
          return; // Prevent invalid selection
        }

        // Save state for undo
        const dayIndex = dayStates.findIndex(day => day.blocks === blocks);
        if (dayIndex !== -1) {
          saveStateForUndo(dayIndex, 'manual');
        }

        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = { ...block, endTime: val };
        setBlocks(newBlocks);
        setIsDirty(true);
      };

      const handleRateChange = (val: string) => {
        const newRate = sanitizeCurrencyInput(val, 150);
        const maxAmount = parseFloat(block.customMax || "0");
        let finalRate = newRate;
        if (newRate && maxAmount && parseFloat(newRate) > maxAmount) {
          finalRate = block.customMax || "";
        }
        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = {
          ...block,
          customRate: finalRate,
        };
        setBlocks(newBlocks);
        setIsDirty(true);
      };
      const handleMaxChange = (val: string) => {
        const newMax = sanitizeCurrencyInput(val, 999);
        const currentRate = parseFloat(block.customRate || "0");
        let adjustedRate = block.customRate;
        if (newMax && currentRate && parseFloat(newMax) < currentRate) {
          adjustedRate = newMax;
        }
        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = {
          ...block,
          customMax: newMax,
          customRate: adjustedRate,
        };
        setBlocks(newBlocks);
        setIsDirty(true);
      };

      // Calculate disabled times for each selector (including .5 increments)
      const startDisabledTimes = block.endTime && block.endTime !== ""
        ? Array.from({ length: 48 }, (_, i) => { // 48 for 30-minute intervals (24 hours * 2)
          const hour = Math.floor(i / 2);
          const minute = (i % 2) * 30;
          if (hour === 24) return "24:00"; // Handle end of day case
          return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        }).filter(time => {
          if (block.endTime === "24:00") return time > "00:00" && time <= "24:00";
          return time >= block.endTime!;
        })
        : [];

      const endDisabledTimes = block.startTime && block.startTime !== ""
        ? Array.from({ length: 48 }, (_, i) => { // 48 for 30-minute intervals (24 hours * 2)
          const hour = Math.floor(i / 2);
          const minute = (i % 2) * 30;
          if (hour === 24) return "24:00"; // Handle end of day case
          return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        }).filter(time => {
          if (time === "24:00") return block.startTime! >= time;
          return time <= block.startTime!;
        })
        : [];

      // Get other time blocks in the same day
      const otherBlocks = blocks
        .filter((b, idx) => idx !== rowIndex &&
          ((b.blockMode === "setTime" && b.startTime && b.endTime) ||
            (b.blockMode === "permitOnly" && b.permitTimeMode === "setTime" && b.startTime && b.endTime)))
        .map(b => ({
          startTime: b.startTime!,
          endTime: b.endTime!
        }));

      return (
        <div className="fixed-box setTime-mode">
          <div className="setTime-middle">
            <TimeSelector
              value={block.startTime ?? ""}
              onChange={handleStartChange}
              disabledTimes={startDisabledTimes}
              otherBlocks={otherBlocks}
              isStartSelector={true}
            />
            <TimeSelector
              value={block.endTime ?? ""}
              onChange={handleEndChange}
              disabledTimes={endDisabledTimes}
              otherBlocks={otherBlocks}
              isStartSelector={false}
            />
          </div>
          <div className="setTime-bottom">
            <div className="price-row">
              <input
                type="text"
                placeholder="Rate"
                value={sanitizeCurrencyInput(block.customRate ?? "", 150)}
                onChange={(e) => handleRateChange(e.target.value)}
                className="block-input"
                maxLength={7}
              />
              <span className="block-unit">$/hr</span>
            </div>
            <div className="price-row">
              <input
                type="text"
                placeholder="Max"
                value={sanitizeCurrencyInput(block.customMax ?? "", 999)}
                onChange={(e) => handleMaxChange(e.target.value)}
                className="block-input-bottom"
                maxLength={7}
              />
              <span className="block-unit">$ Max</span>
            </div>
          </div>
        </div>
      );
    }

    if (block.blockMode === "permitOnly") {
      const handlePermitTimeModeChange = (newMode: "allDay" | "setTime") => {
        const newBlocks = [...blocks] as DayData;
        if (newMode === "allDay") {
          newBlocks[rowIndex] = {
            ...block,
            permitTimeMode: "allDay",
            startTime: "00:00",
            endTime: "23:59",
          };
          // Add default block below when switching to allDay
          if (rowIndex === 0 && newBlocks[1].blockMode === "newBlock") {
            newBlocks[1] = { blockMode: "default", customRate: globalRate, customMax: globalMax };
          }
        } else {
          newBlocks[rowIndex] = {
            ...block,
            permitTimeMode: "setTime",
            startTime: "--",
            endTime: "--",
          };
          // Add default block below when switching to setTime
          if (rowIndex === 0 && newBlocks[1].blockMode === "newBlock") {
            newBlocks[1] = { blockMode: "default", customRate: globalRate, customMax: globalMax };
          }
        }
        setBlocks(newBlocks);
        setIsDirty(true);
      };

      const handleStartChange = (val: string) => {
        // If end time is set and new start time is >= end time, don't allow the change
        if (block.endTime && block.endTime !== "--" && val >= block.endTime) {
          return; // Prevent invalid selection
        }

        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = { ...block, startTime: val };
        setBlocks(newBlocks);
        setIsDirty(true);
      };

      const handleEndChange = (val: string) => {
        // If start time is set and new end time is <= start time, don't allow the change
        if (block.startTime && block.startTime !== "--" && val <= block.startTime) {
          return; // Prevent invalid selection
        }

        const newBlocks = [...blocks] as DayData;
        newBlocks[rowIndex] = { ...block, endTime: val };
        setBlocks(newBlocks);
        setIsDirty(true);
      };

      // Calculate disabled times for permit time selectors (including .5 increments)
      const startDisabledTimes = block.endTime && block.endTime !== "" && block.endTime !== "--"
        ? Array.from({ length: 48 }, (_, i) => { // 48 for 30-minute intervals (24 hours * 2)
          const hour = Math.floor(i / 2);
          const minute = (i % 2) * 30;
          if (hour === 24) return "24:00";
          return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        }).filter(time => {
          if (block.endTime === "24:00") return time > "00:00" && time <= "24:00";
          return time >= block.endTime!;
        })
        : [];

      const endDisabledTimes = block.startTime && block.startTime !== "" && block.startTime !== "--"
        ? Array.from({ length: 48 }, (_, i) => { // 48 for 30-minute intervals (24 hours * 2)
          const hour = Math.floor(i / 2);
          const minute = (i % 2) * 30;
          if (hour === 24) return "24:00";
          return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        }).filter(time => {
          if (time === "24:00") return block.startTime! >= time;
          return time <= block.startTime!;
        })
        : [];

      // Get other time blocks in the same day for overlap validation
      const otherBlocks = blocks
        .filter((b, idx) => idx !== rowIndex &&
          ((b.blockMode === "setTime" && b.startTime && b.endTime) ||
            (b.blockMode === "permitOnly" && b.permitTimeMode === "setTime" && b.startTime && b.endTime)))
        .map(b => ({
          startTime: b.startTime!,
          endTime: b.endTime!
        }));

      return (
        <div className="fixed-box permitOnly-mode">
          <div className="permitOnly-bottom">
            {rowIndex === 0 ? (
              <select
                className="permit-time-mode-dropdown"
                value={block.permitTimeMode || "allDay"}
                onChange={(e) => handlePermitTimeModeChange(e.target.value as "allDay" | "setTime")}
              >
                <option value="allDay">All Day</option>
                <option value="setTime">Set Time</option>
              </select>
            ) : (
              <div className="permit-time-mode-text">Set Time</div>
            )}
            {(block.permitTimeMode === "setTime" || rowIndex > 0) && (
              <div className="permit-time-selectors">
                <div className="permit-start-time-selector">
                  <TimeSelector
                    value={block.startTime ?? ""}
                    onChange={handleStartChange}
                    disabledTimes={startDisabledTimes}
                    otherBlocks={otherBlocks}
                    isStartSelector={true}
                  />
                </div>
                <div className="permit-end-time-selector">
                  <TimeSelector
                    value={block.endTime ?? ""}
                    onChange={handleEndChange}
                    disabledTimes={endDisabledTimes}
                    otherBlocks={otherBlocks}
                    isStartSelector={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  }

  // Show loading indicator while data is being fetched
  if (isLoading) {
    return (
      <div className="advanced-settings-loading">
        <div className="advanced-settings-header">
          <h1>Advanced Settings</h1>
          <div className="slider-wrapper">
            <Slider checked onChange={() => { }} />
          </div>
        </div>
        <p>These settings allow for more customized billing periods. <span className="tutorial-link">Tutorial Video</span></p>

        <div className="advanced-settings-skeleton-grid">
          {dayLabels.map((day) => (
            <div key={day} className="skeleton-block advanced-skeleton-day" aria-hidden="true" />
          ))}
          {Array.from({ length: 21 }).map((_, index) => (
            <div key={index} className="skeleton-block advanced-skeleton-cell" aria-hidden="true" />
          ))}
        </div>

        <div className="advanced-settings-skeleton-footer">
          <div className="skeleton-block advanced-skeleton-button" aria-hidden="true" />
        </div>
      </div>
    );
  }

  // Show error message if there was a problem loading data
  if (error) {
    return <Error message={error} />;
  }

  return (
    <div>
      {/* Feedback tooltip */}
      {showTooltip && (
        <div
          className="CopyPasteTooltip"
          style={{
            position: 'fixed',
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            zIndex: 1000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          {tooltipMessage}
        </div>
      )}

      <div className="advanced-settings-header">
        <h1>Advanced Settings</h1>
        <div className="slider-wrapper">
          <Slider checked={advancedEnabled} onChange={handleToggleAdvanced} />
        </div>
      </div>

      {!advancedEnabled && (
        <p>Enable advanced settings to control time of day or day of week pricing.</p>
      )}
      {advancedEnabled && (
        <>
          <p>These settings allow for more customized billing periods. <span className="tutorial-link" onClick={() => setModalType("tutorialVideo")}>Tutorial Video</span></p>

          <div className="advanced-grid" style={{ position: "relative" }}>
            {/* Walkthrough anchor: absolutely positioned over Monday's column. */}
            <div
              data-walkthrough="advanced-monday-column"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: `calc(100% / ${dayLabels.length})`,
                height: "100%",
                pointerEvents: "none",
              }}
              aria-hidden="true"
            />
            {/* Row 1: day labels */}
            {dayLabels.map((day, index) => (
              <div
                key={day}
                className="day-label"
                onMouseEnter={() => setHoveredDayIndex(index)}
                onMouseLeave={() => setHoveredDayIndex(null)}
                data-day-index={index}
              >
                {day}
              </div>
            ))}

            {/* Rows 2..4 => blocks */}
            {[0, 1, 2].map((rowIndex) =>
              dayStates.map(({ label, blocks, setter }, dayIndex) => {
                // auto-check coverage before rendering rowIndex=2
                if (rowIndex === 2) {
                  maybeAdjustThirdRow(blocks);
                }
                const block = blocks[rowIndex];

                const walkthroughAttr =
                  dayIndex === 0 && rowIndex === 0
                    ? "advanced-first-block"
                    : dayIndex === 0 && rowIndex === 1
                      ? "advanced-default-block"
                      : undefined;

                // If it's a newBlock, render a simplified div directly with new-block styling
                if (block.blockMode === "newBlock") {
                  return (
                    <div
                      className="new-block"
                      key={label + rowIndex}
                      data-walkthrough={walkthroughAttr}
                      onMouseEnter={() => setHoveredDayIndex(dayIndex)}
                      onMouseLeave={() => setHoveredDayIndex(null)}
                    >
                      New Time
                      Block
                    </div>
                  );
                }

                return (
                  <div
                    className="cell"
                    key={label + rowIndex}
                    data-walkthrough={walkthroughAttr}
                    onMouseEnter={() => setHoveredDayIndex(dayIndex)}
                    onMouseLeave={() => setHoveredDayIndex(null)}
                  >
                    {renderBlockDropdown(block, rowIndex, blocks, setter)}
                    {renderBlockContent(block, rowIndex, blocks, setter)}
                  </div>
                );
              })
            )}
          </div>

          <div className="advanced-settings-buttons">
            <button
              className="button-primary button-icon"
              onClick={handleSaveClick}
              disabled={isSaving || !validateSetTimeBlocks() || !isDirty}
              style={{ opacity: !isSaving && validateSetTimeBlocks() && isDirty ? 1 : 0.3 }}
            >
              <img
                src={!isSaving && validateSetTimeBlocks() && isDirty ? "/assets/Save.svg" : "/assets/SaveDisabled.svg"}
                alt="Save"
              />
              <span>{isSaving ? "Saving..." : "Save"}</span>
            </button>
            {hasGridModifications() && (
              <button
                className="button-secondary button-icon"
                onClick={handleReset}
                disabled={isSaving}
              >
                <img src="/assets/reset.svg" alt="Reset" />
                <span>Reset</span>
              </button>
            )}
          </div>
        </>
      )}

      {modalType === "disableAdvanced" && (
        <Modal
          isOpen
          title="Disable Advanced Settings?"
          description="This removes custom day/time pricing and reverts to General Settings."
          confirmText="Disable"
          cancelText="Cancel"
          onConfirm={() => {
            confirmDisableAdvanced();
            setModalType(null);
          }}
          onCancel={() => setModalType(null)}
        />
      )}
      {modalType === "confirmSave" && (
        <Modal
          isOpen
          title="Confirm Changes"
          description="By continuing, you acknowledge that you are not misinforming your customers of pricing and that this change is being recorded in case of disputes. Current parked vehicles will not be affected by these changes."
          confirmText={isSaving ? "Saving..." : "Update Settings"}
          cancelText="Cancel"
          onConfirm={() => {
            doSave();
            setIsAcknowledged(false);
          }}
          onCancel={() => {
            setModalType(null);
            setIsAcknowledged(false);
          }}
          disableConfirm={!isAcknowledged || isSaving}
        >
          <div className="settings-acknowledge">
            <input
              type="checkbox"
              id="acknowledge-checkbox"
              checked={isAcknowledged}
              onChange={(e) => setIsAcknowledged(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="acknowledge-checkbox" style={{ cursor: 'pointer' }}>
              I acknowledge
            </label>
          </div>
        </Modal>
      )}
      {modalType === "unsavedChanges" && (
        <Modal
          isOpen
          title="You have unsaved changes!"
          description="Changes will not be applied unless you save before leaving."
          confirmText="Take me back!"
          cancelText="Continue Anyway"
          onConfirm={() => setModalType(null)}
          onCancel={() => {
            if (pendingAction) {
              pendingAction();
              setPendingAction(null);
            }
            setModalType(null);
          }}
        />
      )}
      {modalType === "confirmReset" && (
        <Modal
          isOpen
          title="Reset Advanced Settings"
          description="All current settings will return to default and not be recoverable."
          confirmText="Reset"
          cancelText="Cancel"
          onConfirm={() => {
            confirmReset();
          }}
          onCancel={() => setModalType(null)}
        />
      )}
      {modalType === "tutorialVideo" && (
        <VideoModal
          isOpen
          title="Advanced Settings Tutorial"
          videoUrl="https://www.youtube.com/watch?v=HoAepPFQdG8"
          onClose={() => setModalType(null)}
        />
      )}
    </div>
  );
};

export default AdvancedSettings;
